import React, { useEffect, useRef, useState } from "react";
import styles from "./TemplateList.module.scss";
import DeleteIcon from "@mui/icons-material/Delete";
import { useMutation } from "@tanstack/react-query";
import { confirmAlert } from "react-confirm-alert";
import axios from "axios";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import DoneIcon from "@mui/icons-material/Done";
import PersonIcon from "@mui/icons-material/Person";
import Checkbox from "@mui/material/Checkbox";
import WarningIcon from "@mui/icons-material/Warning";
import Groups2Icon from "@mui/icons-material/Groups2";
import Lottie from "lottie-react";
import videoLoadingAnimation from "../animation/Animation - 1741783692232.json";

const TemplateList = ({
  defaultTemplates,
  nonDefaultTemplates,
  onSelect,
  isLoading,
  selectedTemplateId,
  globalTemplates,
}) => {
  const templateRefs = useRef({});
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [filter, setFilter] = useState("all");
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState([]);

  const deleteConsentTemplate = useMutation(
    (id) => axios.delete(`/consent_clause/${id}`),
    {
      onSuccess: (data, id) => {
        if (selectedTemplateId === id) {
          onSelect(null);
        }
        window.location.reload();
      },
    }
  );

  const handleDelete = (e, templateId) => {
    e.stopPropagation();
    confirmAlert({
      title: "Confirm to delete",
      message: "Are you sure you want to delete this consent clause?",
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            deleteConsentTemplate.mutate(templateId);
          },
        },
        {
          label: "No",
          onClick: () => {},
        },
      ],
    });
  };

  const deleteBulk = useMutation(
    () =>
      axios.post(`/consent_clause/bulk_archive_consent_clauses`, {
        ids: selectedTemplates,
      }),
    {
      onSuccess: () => {
        setSelectedTemplates([]);
        window.location.reload();
      },
      onError: (error) => {
        console.error("Error archiving templates:", error);
      },
    }
  );

  const handleDeleteBulk = () => {
    confirmAlert({
      title: "Confirm Bulk Archive",
      message: "Are you sure you want to archive the selected templates?",
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            deleteBulk.mutate();
          },
        },
        {
          label: "No",
          onClick: () => {},
        },
      ],
    });
  };

  const cloneConsentTemplate = useMutation(
    (clause) => axios.put(`/consent_clause/${clause.id}/clone`),
    {
      onSuccess: async (data, clause) => {
        setCurrentTemplate(data.data);
        scrollToTop();

        setTimeout(() => {
          onSelect(data.data);
        }, 1200);

        try {
          const { data: updatedTemplates } = await axios.get(`/consent_clause?=`);
          onSelect(updatedTemplates);
        } catch (error) {
          console.error("Error fetching updated templates:", error);
        }
      },
    }
  );

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const scrollToSelectedTemplate = () => {
      if (selectedTemplateId && templateRefs.current[selectedTemplateId]) {
        templateRefs.current[selectedTemplateId].scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }
    };

    if (selectedTemplateId) {
      const timeoutId = setTimeout(scrollToSelectedTemplate, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [selectedTemplateId]);

  if (isLoading) {
    return (
      <div className={styles.loaderWrap}>
        <Lottie
          animationData={videoLoadingAnimation}
          className={styles.loaderAnimation}
        />
        <img
          src="/logo.png"
          alt="Overlay logo"
          className={styles.loaderLogo}
        />
      </div>
    );
  }

  const handleTemplateClick = (template) => {
    setSelectedTemplateIds((prev) =>
      prev.includes(template.id)
        ? prev.filter((id) => id !== template.id)
        : [...prev, template.id]
    );

    onSelect(template);
  };

  const handleCheckboxChange = (templateId) => {
    setSelectedTemplates((prevSelected) =>
      prevSelected.includes(templateId)
        ? prevSelected.filter((id) => id !== templateId)
        : [...prevSelected, templateId]
    );
  };

  const filteredTemplates = (() => {
    if (filter === "default") return defaultTemplates;
    if (filter === "archived") return nonDefaultTemplates;
    if (filter === "global") return globalTemplates;
    return [...defaultTemplates, ...nonDefaultTemplates, ...globalTemplates];
  })();

  const getLevelLabel = (template) => {
    if (template.level === "user") return "User";
    if (template.level === "practice") return "Practice";
    if (template.level === "global") return "Global";
    return "Template";
  };

  return (
    <div className={styles.templateListBox}>
      {selectedTemplates.length > 0 && (
        <div className={styles.bulkActionBar}>
          <button
            className={styles.actionButton}
            onClick={handleDeleteBulk}
          >
            <span className={styles.buttonContent}>
              Archive Selected
              <WarningIcon fontSize="small" />
            </span>
          </button>
        </div>
      )}

      <div className={styles.filterButtons}>
        <button
          onClick={() => setFilter("all")}
          className={`${styles.filterButton} ${filter === "all" ? styles.active : ""}`}
        >
          All
        </button>
        <button
          onClick={() => setFilter("default")}
          className={`${styles.filterButton} ${filter === "default" ? styles.active : ""}`}
        >
          Default
        </button>
        <button
          onClick={() => setFilter("global")}
          className={`${styles.filterButton} ${filter === "global" ? styles.active : ""}`}
        >
          Global
        </button>
        <button
          onClick={() => setFilter("archived")}
          className={`${styles.filterButton} ${filter === "archived" ? styles.active : ""}`}
        >
          Archived
        </button>
      </div>

      <div className={styles.scrollableContainer}>
        {filteredTemplates.map((template) => {
          const isSelected = template.id === selectedTemplateId;
          const isIntroOutro =
            template.type === "intro" || template.type === "outro";
          const isGlobal = template.level === "global";
          const canShowCheckbox = !isIntroOutro && !isGlobal;
          const canDelete = !["intro", "outro"].includes(template.type) && !isGlobal;
          const canClone = !["intro", "outro"].includes(template.type) && !isGlobal;

          return (
            <div
              key={template.id}
              className={`${styles.listItem} ${
                isSelected ? styles.selected : ""
              } ${isIntroOutro ? styles.highlighted : ""} ${
                isGlobal ? styles.globalCard : ""
              }`}
              onClick={() => handleTemplateClick(template)}
              ref={(el) => (templateRefs.current[template.id] = el)}
            >
              <div className={styles.rowLeft}>
                {canShowCheckbox ? (
                  <div
                    className={styles.checkboxContainer}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={selectedTemplates.includes(template.id)}
                      onChange={() => handleCheckboxChange(template.id)}
                      size="small"
                    />
                  </div>
                ) : (
                  <div className={styles.checkboxSpacer} />
                )}

                <div className={styles.iconStack}>
                  {template.is_default ? (
                    <div className={styles.defaultBadge}>
                      <DoneIcon className={styles.defaultIcon} />
                    </div>
                  ) : (
                    <div className={styles.defaultBadgePlaceholder} />
                  )}

                  <div className={styles.levelIconWrap}>
                    {template.level === "user" ? (
                      <PersonIcon className={styles.userIcon} />
                    ) : template.level === "practice" ? (
                      <Groups2Icon className={styles.buildingIcon} />
                    ) : (
                      <div className={styles.publicContainer}>
                        <img
                          src="/logo_mono.png"
                          alt="Global template"
                          className={styles.smallImage}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.content}>
                <div className={styles.titleRow}>
                  <span className={styles.templateName}>{template.name}</span>
                  {isIntroOutro && (
                    <span className={styles.typeBadge}>
                      {template.type}
                    </span>
                  )}
                </div>

                <div className={styles.metaRow}>
                  <span className={styles.levelBadge}>{getLevelLabel(template)}</span>
                  {template.is_default && (
                    <span className={styles.defaultTextBadge}>Default</span>
                  )}
                </div>
              </div>

              <div className={styles.rightSide}>
                {canClone && (
                  <button
                    type="button"
                    className={styles.iconButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      cloneConsentTemplate.mutate(template);
                    }}
                    title="Clone template"
                  >
                    <FileCopyIcon className={styles.copyIcon} />
                  </button>
                )}

                {canDelete && (
                  <button
                    type="button"
                    className={`${styles.iconButton} ${styles.deleteButton}`}
                    onClick={(e) => handleDelete(e, template.id)}
                    title="Delete template"
                  >
                    <DeleteIcon className={styles.deleteIcon} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TemplateList;